<template>
  <div class="impact-hints-edition">

    <ImpactSelector v-model="previewImpact" min class="impact-hints-edition__selector" />

    <EditableLabel
      v-model="currentHint"
      class="impact-hints-edition__preview-label"
      :legend="`${$t('message.ENTER_IMPACT_HINT')}${$t('message.TWO_DOTS')}`"
      use-textarea
    />

    <div
      v-for="(hint, idx) in hintsCopy"
      :key="idx"
      class="impact-hints-edition__row v-centerer generic-border--thin-border"
    >
      <Icon @click.native="removeHint(idx)">{{ deleteMedium }}</Icon>
      <div class="generic-border generic-border--thin-border impact-hints-edition__row__impact">
        {{ hint.impact + 1 }}
      </div>
      <div class="impact-hints-edition__row__text">
        {{ hint.text }}
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Components to edit the hints of an impact slider
 *
 * @vue-prop {Object} hints - Hints to customize : [{ impact: 0, text: 'hint' }, { ... }]
 *
 * @vue-event {Object} hintsUpdated - hints value has been updated
 */

import { cloneDeep } from 'lodash';
// Components
import EditableLabel from '@/components/editable-label.vue';
import Icon from '@/components/icon.vue';
import ImpactSelector from '@/components/impact-selector.vue';
// Utils/Assets
import { deleteMedium } from '@/assets/icons';

export default {

  name: 'ImpactHintsEdition',

  components: {
    EditableLabel,
    Icon,
    ImpactSelector,
  },

  props: {
    hints: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      deleteMedium,
      // Use a copy to prevent delay between change and dom update
      hintsCopy: [],
      previewImpact: 0,
    };
  },

  computed: {
    /** String: Display the hint of the currently selected imapct */
    impactHint() {
      const hint = this.hintsCopy.find(h => h.impact === this.previewImpact);
      return hint ? hint.text : null;
    },
    /** String: Hint of the currently selected impact */
    currentHint: {
      get() {
        const hint = this.hintsCopy.find(h => h.impact === this.previewImpact);
        return hint ? hint.text : '';
      },
      set(newHint) {
        const hint = this.hintsCopy.find(h => h.impact === this.previewImpact);
        if (!hint) {
          this.hintsCopy.push({ impact: this.previewImpact, text: newHint });
        } else {
          hint.text = newHint;
        }
        this.$emit('hintsUpdated', this.hintsCopy);
      },
    },
  },

  watch: {
    hints: {
      deep: true,
      handler(newValue) {
        this.hintsCopy = cloneDeep(newValue || []);
      },
    },
  },

  created() {
    this.hintsCopy = cloneDeep(this.hints || []);
  },

  methods: {
    /** Remove hint at index */
    removeHint(index) {
      this.hintsCopy.splice(index, 1);
      this.$emit('hintsUpdated', this.hintsCopy);
    },
  },

};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.impact-hints-edition {

  &__selector {
    margin-top: 1rem;
  }

  &__preview-label {
    margin: 1rem 0 2rem 0;
  }

  &__row {
    margin-bottom: .5rem;

    .icon {
      cursor: pointer;
      width: 1.4em;
      height: 1.4em;
      border-radius: 100px;
      padding: .5rem;
      flex: 0 0 auto;

      &:hover {
        background: $color-shizen-red-light;
      }
    }

    &__impact {
      flex: 0 0 auto;
      margin: 0 1rem;
    }

    &__text {
      flex: 1;
      min-width: 0px;
      white-space: pre-line;
    }
  }
}
</style>
